import type { MetricsQueryResponse, MetricName } from "../api/types";
import { formatBytes, formatNumber, formatPercent } from "../lib/format";
import type { ChartPoint } from "../components/charts/TimeSeriesChart";

/** Metrik adı → Türkçe etiket. */
const metricLabels: Record<MetricName, string> = {
  "cpu.usage_percent": "CPU kullanımı",
  "cpu.load1": "CPU yükü (1 dk)",
  "memory.usage_percent": "Bellek kullanımı",
  "memory.used_bytes": "Kullanılan bellek",
  "swap.usage_percent": "Swap kullanımı",
  "disk.usage_percent": "Disk kullanımı",
  "disk.read_bytes": "Disk okuma",
  "disk.write_bytes": "Disk yazma",
  "net.rx_bytes": "Ağ girişi (RX)",
  "net.tx_bytes": "Ağ çıkışı (TX)",
  "system.uptime_seconds": "Çalışma süresi",
  "system.process_count": "Süreç sayısı",
};

export function metricLabel(metric: MetricName): string {
  return metricLabels[metric] ?? metric;
}

/** Yüzde metrikleri. */
export function isPercentMetric(metric: MetricName): boolean {
  return metric.endsWith("_percent");
}

/** Kümülatif sayaç metrikleri (grafikte hıza çevrilir). */
export function isCounterMetric(metric: MetricName): boolean {
  return (
    metric === "disk.read_bytes" ||
    metric === "disk.write_bytes" ||
    metric === "net.rx_bytes" ||
    metric === "net.tx_bytes"
  );
}

export function formatMetricValue(metric: MetricName, value: number): string {
  if (isPercentMetric(metric)) return formatPercent(value);
  if (isCounterMetric(metric)) return formatBytes(value);
  if (metric === "memory.used_bytes") return formatBytes(value);
  if (metric === "system.uptime_seconds") {
    const days = Math.floor(value / 86400);
    const hours = Math.floor((value % 86400) / 3600);
    return days > 0 ? `${days}g ${hours}s` : `${hours}s`;
  }
  return formatNumber(value, 1);
}

/** Seriyi grafik noktasına çevirir. */
export function toChartPoints(response: MetricsQueryResponse): ChartPoint[] {
  return response.points.map((point) => ({
    time: Date.parse(point.time),
    value: point.value,
  }));
}

/**
 * Kümülatif sayaç serisini "birim/s" hız serisine çevirir.
 * (openapi sayaç değerleri tanımlar; panel hız gösterir.)
 */
export function toRatePoints(response: MetricsQueryResponse): ChartPoint[] {
  const points = response.points;
  const rates: ChartPoint[] = [];
  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    const deltaSeconds =
      (Date.parse(current.time) - Date.parse(previous.time)) / 1000;
    if (deltaSeconds <= 0) continue;
    rates.push({
      time: Date.parse(current.time),
      value: Math.max(0, (current.value - previous.value) / deltaSeconds),
    });
  }
  return rates;
}

/** Metrik türüne göre grafik serisi hazırlar. */
export function seriesForMetric(response: MetricsQueryResponse): ChartPoint[] {
  return isCounterMetric(response.metric) ? toRatePoints(response) : toChartPoints(response);
}

/** Farklı cihaz serilerini zaman dilimlerine göre ortalar (filo trendi). */
export function averageSeries(
  responses: MetricsQueryResponse[],
): ChartPoint[] {
  const buckets = new Map<number, { total: number; count: number }>();
  for (const response of responses) {
    for (const point of response.points) {
      const time = Date.parse(point.time);
      const bucket = buckets.get(time) ?? { total: 0, count: 0 };
      bucket.total += point.value;
      bucket.count += 1;
      buckets.set(time, bucket);
    }
  }
  return [...buckets.entries()]
    .map(([time, bucket]) => ({ time, value: bucket.total / bucket.count }))
    .sort((a, b) => a.time - b.time);
}

/* ------------------------------------------------------------------ */
/* Zaman aralığı seçici                                                */
/* ------------------------------------------------------------------ */

export type RangeKey = "1h" | "6h" | "24h" | "7d";

export const RANGE_OPTIONS: ReadonlyArray<{ value: RangeKey; label: string }> = [
  { value: "1h", label: "1 saat" },
  { value: "6h", label: "6 saat" },
  { value: "24h", label: "24 saat" },
  { value: "7d", label: "7 gün" },
];

const rangeMs: Record<RangeKey, number> = {
  "1h": 60 * 60 * 1000,
  "6h": 6 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
};

export const rangeIntervalSeconds: Record<RangeKey, number> = {
  "1h": 60,
  "6h": 300,
  "24h": 300,
  "7d": 1800,
};

/** Zaman aralığından sorgu penceresini üretir. */
export function rangeWindow(range: RangeKey): {
  from: string;
  to: string;
  intervalSeconds: number;
} {
  const to = Date.now();
  const from = to - rangeMs[range];
  return {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    intervalSeconds: rangeIntervalSeconds[range],
  };
}

/** Grafik x ekseni biçimini aralığa göre seçer. */
export function timeFormatterForRange(range: RangeKey): (time: number) => string {
  return (time: number) => {
    const date = new Date(time);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    if (range === "7d") {
      return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}`;
    }
    return `${hours}:${minutes}`;
  };
}